import { type FC, useEffect, useRef, useState } from 'react'
import './_verificationCode.scss'
import { useTranslation } from 'react-i18next'
import { useTimer } from 'react-timer-hook'

import { Button } from 'components/Button/Button'
import { addToast } from 'components/Toast/Toast'
import { sendDeleteCode } from 'services/endpoints/profile'
import { useUserProfile } from 'store'

const VerificationCode: FC<{ changeFn?: (val: string) => void; email?: any }> = ({
    changeFn,
    email
}) => {
    const userData = useUserProfile()
    const [sending, setSending] = useState(false)

    const [code, setCode] = useState({
        digit1: '',
        digit2: '',
        digit3: '',
        digit4: '',
        digit5: '',
        digit6: ''
    })

    useEffect(() => {
        if (changeFn) {
            changeFn(
                `${code.digit1}${code.digit2}${code.digit3}${code.digit4}${code.digit5}${code.digit6}`
            )
        }
    }, [code, changeFn])

    const [hasSent, setHasSent] = useState(false)
    const [cooldown, setCooldown] = useState(false)

    const { t } = useTranslation()

    const time = new Date()
    time.setSeconds(time.getSeconds() + 30)

    const { seconds, restart } = useTimer({
        expiryTimestamp: time,
        onExpire: () => setCooldown(false)
    })

    const dig1Ref = useRef<HTMLInputElement>(null)
    const dig2Ref = useRef<HTMLInputElement>(null)
    const dig3Ref = useRef<HTMLInputElement>(null)
    const dig4Ref = useRef<HTMLInputElement>(null)
    const dig5Ref = useRef<HTMLInputElement>(null)
    const dig6Ref = useRef<HTMLInputElement>(null)

    const refs = [dig1Ref, dig2Ref, dig3Ref, dig4Ref, dig5Ref, dig6Ref]

    const digitKeys = [
        'digit1',
        'digit2',
        'digit3',
        'digit4',
        'digit5',
        'digit6'
    ] as const

    const updateCode = (index: number, value: string) => {
        // Only allow numbers
        const digit = value.replace(/\D/g, '').slice(-1)

        setCode(prev => ({
            ...prev,
            [digitKeys[index]]: digit
        }))

        // Move forward after typing a digit
        if (digit && index < refs.length - 1) {
            refs[index + 1].current?.focus()
        }
    }

    const handleKeyDown = (
        e: React.KeyboardEvent<HTMLInputElement>,
        index: number
    ) => {
        if (e.key !== 'Backspace') return

        const currentKey = digitKeys[index]
        const currentValue = code[currentKey]

        // If current input has a value, normal Backspace clears it.
        if (currentValue) {
            return
        }

        // If current input is already empty, go back and clear previous digit.
        if (index > 0) {
            e.preventDefault()

            const previousKey = digitKeys[index - 1]

            setCode(prev => ({
                ...prev,
                [previousKey]: ''
            }))

            refs[index - 1].current?.focus()
        }
    }

    const handlePaste = (
        e: React.ClipboardEvent<HTMLInputElement>,
        startIndex: number
    ) => {
        e.preventDefault()

        const pasted = e.clipboardData
            .getData('text')
            .replace(/\D/g, '')

        if (!pasted) return

        const digits = pasted.split('').slice(0, refs.length - startIndex)

        setCode(prev => {
            const next = { ...prev }

            digits.forEach((digit, offset) => {
                next[digitKeys[startIndex + offset]] = digit
            })

            return next
        })

        // Focus the input immediately after the pasted digits,
        // or the last input if all 6 were filled.
        const nextIndex = Math.min(
            startIndex + digits.length,
            refs.length - 1
        )

        refs[nextIndex].current?.focus()
    }

    const inputBlocks = digitKeys.map((key, index) => ({
        id: index + 1,
        type: 'text',
        value: code[key],
        ref: refs[index],
        index
    }))

    const locationRef = useRef(false)

    useEffect(() => {
        if (locationRef.current) return

        const timer = setTimeout(() => {
            locationRef.current = true
            setSending(true)
            setHasSent(true)
            setCooldown(true)

            sendDeleteCode(email)
                .then(() => {
                    const newTime = new Date()
                    newTime.setSeconds(newTime.getSeconds() + 30)
                    restart(newTime)
                })
                .catch(() => {
                    setHasSent(false)
                    setCooldown(false)
                    addToast('error', t('errorWhileSendingCode'))
                })
                .finally(() => {
                    setSending(false)
                })
        }, 100)

        return () => clearTimeout(timer)
    }, [])

    const handleResend = () => {
        setSending(true)
        setHasSent(true)
        setCooldown(true)

        sendDeleteCode(email)
            .then(() => {
                const newTime = new Date()
                newTime.setSeconds(newTime.getSeconds() + 30)
                restart(newTime)
            })
            .catch(() => {
                addToast('error', t('errorWhileSendingCode'))
            })
            .finally(() => {
                setSending(false)
            })
    }

    return (
        <div className='verificationCode'>
            <p className='verificationCode__title'>{t('verificationCode')}</p>

            <div className='verificationCode__input'>
                {inputBlocks.map(block => (
                    <input
                        key={block.id}
                        type={block.type}
                        value={block.value}
                        maxLength={1}
                        inputMode='numeric'
                        autoComplete={block.index === 0 ? 'one-time-code' : 'off'}
                        onChange={e => updateCode(block.index, e.currentTarget.value)}
                        onKeyDown={e => handleKeyDown(e, block.index)}
                        onPaste={e => handlePaste(e, block.index)}
                        ref={block.ref}
                        className={`verificationCode__input__block${
                            block.value
                                ? ' verificationCode__input__block--filled'
                                : ''
                        }`}
                    />
                ))}
            </div>

            <div className='verificationCode__actions'>
                {hasSent ? (
                    <div className='verificationCode__actions__codesent'>
                        <div className='codeWasSentTo'>
                        {t('codeWasSentTo')}{' '}
                        <span>{email || userData?.email}</span>
                        </div>
                        {cooldown ? (
                            <>
                                {t('Resend Code')} <span>{seconds}</span>
                            </>
                        ) : (
                            <Button
                                text={t('Re-Send')}
                                color='grey'
                                font='mont-14-normal'
                                width='13'
                                height='3'
                                disabled={sending}
                                clickFn={handleResend}
                                customClass='verificationCode__actions__button'
                            />
                        )}
                    </div>
                ) : null}
            </div>
        </div>
    )
}

export default VerificationCode